/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
/* eslint-disable no-multi-spaces */
import {SVG} from '@svgdotjs/svg.js';
import {getAssetState} from '@ion/core/src/entities/Asset';
import {AssetType, BaseAsset, CVSAssetType, GenericObject} from '@ion/core/src/types/entities';
import {assetStateColorMap} from '@ion/dls/src/helpers/assetStateColorMap';
import {dls} from '@ion/dls/src/theme/theme';
import {IconStatus} from '@ion/dls/src/types/types';
import '@svgdotjs/svg.filter.js';

/**
 * Defines the asset marker size which is used to define the width and height
 * of an asset marker when displayed in detailed mode.
 */
export enum AssetMarkerSize {
  width = 148,
  height = 40
};

/**
 * Defines the AssetMarkerStates which are used to determine the icon which
 * is to be rendered based on the state of the asset's marker within the map.
 */
export enum AssetMarkerStates {
  DEFAULT  = 'Default',
  SELECTED = 'Selected'
};

// defines the asset prop structure...
export interface AssetMarker extends CVSAssetType {
  iconStatus?: IconStatus;
  assetName?: string;
  geometry?: {
    coordinates: number[];
  };
  drawCustomIcon?: Function;
  customIconProps?: GenericObject;
};

// Parameters for the createMarker() function.
export interface CreateMarkerParams {
  asset?: AssetMarker,
  Graphic: any,
  color?: any,
  selectedAsset?: any,
  markerOnly?: boolean,
  includeIcon?: boolean
};

// Parameters for the createMarkers() function.
export interface CreateMarkersParams extends CreateMarkerParams {
  assets: AssetMarker[]
};

const {white, black, grey4} = dls.colors;

/**
 * Defines the default factors
 * TODO: Once we figured out the styling this enum will not
 * needed
 */
enum defaultFactor {
  lengthLimit = 20,
  minimumHeight = 50,
  label = 60,
  charWidth = 4
};

/**
 * Defines the default position for the viewBox, type, text
 * scale, icon, last position component.
 */
enum defaultPosition {
  viewBox = 80,
  type = -5,
  textWithIcon = 40,
  textWithoutIcon = 25,
  scale = 0.48,
  icon = 9,
  lastPosition = 21
};

/**
 * Defines the Vehicle icon SVG generator function for assets of type 'Vehicle'.
 */
const vehicleIcon = [
  'M32.0149 20.6211H23.0065C22.7396 20.6211 22.4836 20.7363 22.2948 20.9414C22.106 21.1465 22 21.4246 22 21.7146V29.7742C22 29.9177 22.0261 30.0597 22.0767 30.1922C22.1273 30.3247 22.2015 30.4451 22.295 30.5464C22.3885 30.6477 22.4995 30.7279 22.6216 30.7826C22.7437 30.8372 22.8745 30.8651 23.0065 30.8647H24.585L24.6131 30.7885C24.7254 30.4416 24.934 30.1411 25.2102 29.9285C25.4865 29.7159 25.8166 29.6017 26.1551 29.6017C26.4936 29.6017 26.8238 29.7159 27.1 29.9285C27.3763 30.1411 27.5849 30.4416 27.6972 30.7885L27.7224 30.8647H32.0233C32.0644 30.8677 32.1056 30.8677 32.1467 30.8647H33.0299V23.463V21.7268C33.0314 21.5814 33.0062 21.4372 32.9557 21.3025C32.9053 21.1678 32.8306 21.0454 32.7361 20.9424C32.6416 20.8395 32.5291 20.758 32.4053 20.7029C32.2815 20.6477 32.1488 20.6199 32.0149 20.6211Z',
  'M26.1659 32.7359C26.8395 32.7359 27.3855 32.1427 27.3855 31.4109C27.3855 30.6792 26.8395 30.0859 26.1659 30.0859C25.4923 30.0859 24.9463 30.6792 24.9463 31.4109C24.9463 32.1427 25.4923 32.7359 26.1659 32.7359Z',
  'M36.3739 32.7359C37.0475 32.7359 37.5935 32.1427 37.5935 31.4109C37.5935 30.6792 37.0475 30.0859 36.3739 30.0859C35.7003 30.0859 35.1543 30.6792 35.1543 31.4109C35.1543 32.1427 35.7003 32.7359 36.3739 32.7359Z',
  'M33.751 23.4632V30.8529H34.752L34.7744 30.7889C34.8906 30.433 35.1054 30.1249 35.3894 29.907C35.6733 29.6891 36.0123 29.5721 36.36 29.5721C36.7076 29.5721 37.0466 29.6891 37.3306 29.907C37.6145 30.1249 37.8293 30.433 37.9455 30.7889L37.9679 30.8529H39.7399C39.8085 30.8529 39.8744 30.8234 39.9232 30.771C39.9721 30.7185 39.9999 30.6472 40.0006 30.5726V27.3835C40.0009 27.3121 39.9757 27.2434 39.9305 27.1916L37.4688 23.6735C37.4094 23.603 37.337 23.5468 37.2564 23.5085C37.1757 23.4703 37.0886 23.4507 37.0006 23.4512H33.751V23.4632Z'
];

/**
* Defines the Machine icon SVG generator function for assets of type 'Machine'.
*/
const machineIcon = [
  'M38.9336 34.801H27.5459C27.4059 34.8015 27.2671 34.773 27.1377 34.7173C27.0082 34.6615 26.8906 34.5796 26.7916 34.4763C26.6926 34.3729 26.6141 34.2501 26.5607 34.115C26.5073 33.9798 26.4801 33.835 26.4805 33.6888V33.6888C26.4795 33.4817 26.534 33.2785 26.6377 33.102C26.7414 32.9255 26.8902 32.7827 27.0674 32.6897L28.7963 31.784C28.9431 31.7045 29.1062 31.6634 29.2716 31.6641H37.3387C37.5136 31.6643 37.6858 31.7101 37.8395 31.7973L39.4344 32.6831C39.6053 32.7777 39.7482 32.9191 39.8477 33.0923C39.9472 33.2654 39.9995 33.4636 39.999 33.6655V33.6821C40.0003 33.8289 39.9737 33.9744 39.9206 34.1103C39.8676 34.2463 39.7893 34.3698 39.6902 34.4739C39.5911 34.578 39.4732 34.6605 39.3433 34.7167C39.2134 34.7728 39.0742 34.8015 38.9336 34.801V34.801Z',
  'M23.6896 29.2689C24.6162 29.2689 25.3674 28.4847 25.3674 27.5173C25.3674 26.5499 24.6162 25.7656 23.6896 25.7656C22.7629 25.7656 22.0117 26.5499 22.0117 27.5173C22.0117 28.4847 22.7629 29.2689 23.6896 29.2689Z',
  'M36.7994 30.044V23.1906C36.7994 23.0696 36.7534 22.9536 36.6714 22.868C36.5895 22.7824 36.4783 22.7344 36.3624 22.7344H33.0896C32.9903 22.7343 32.8921 22.7556 32.801 22.7968C32.7099 22.838 32.6279 22.8984 32.5601 22.9741L29.7594 26.8204C29.7078 26.8782 29.6792 26.9545 29.6797 27.0336V30.5302C29.6797 30.5709 29.6874 30.6111 29.7023 30.6487C29.7172 30.6863 29.739 30.7204 29.7666 30.7492C29.7941 30.7779 29.8268 30.8008 29.8628 30.8163C29.8988 30.8319 29.9374 30.8399 29.9763 30.8399H31.9891H36.0498C36.1493 30.8399 36.2479 30.8192 36.3396 30.7791C36.4314 30.7389 36.5146 30.6801 36.5844 30.606C36.6542 30.5319 36.7092 30.4441 36.7461 30.3476C36.783 30.2511 36.8011 30.1479 36.7994 30.044V30.044Z',
  'M31.9519 24.0636L31.0524 26.3947C31.0077 26.5112 30.9024 26.5745 30.8227 26.5345L22.0825 22.1654C22.044 22.1371 22.0167 22.0952 22.0055 22.0474C21.9944 21.9997 22.0002 21.9493 22.0219 21.9057L22.4621 20.7668C22.4755 20.7195 22.5048 20.6789 22.5448 20.6525C22.5847 20.6262 22.6324 20.6159 22.679 20.6236L31.8785 23.7972C31.922 23.8224 31.9541 23.8646 31.9679 23.9146C31.9816 23.9645 31.9759 24.0181 31.9519 24.0636V24.0636Z',
  'M24.1629 22.1387H23.2188V26.3513H24.1629V22.1387Z'
];

/**
* Defines the Tool icon SVG generator function for assets of type 'Tool'.
*/
const toolIcon = [
  'M25.0811 32.664C25.0771 32.6924 25.0771 32.7212 25.0811 32.7496V35.3184C25.0811 35.3945 25.0955 35.4698 25.1234 35.54C25.1513 35.6102 25.1923 35.6738 25.2438 35.7272C25.2953 35.7806 25.3564 35.8227 25.4235 35.851C25.4906 35.8793 25.5624 35.8933 25.6347 35.8922H31.2523C31.3246 35.8933 31.3964 35.8793 31.4636 35.851C31.5307 35.8227 31.5918 35.7806 31.6433 35.7272C31.6948 35.6738 31.7357 35.6102 31.7637 35.54C31.7916 35.4698 31.806 35.3945 31.8059 35.3184V33.2805C31.806 33.2044 31.7916 33.1291 31.7637 33.059C31.7357 32.9888 31.6948 32.9252 31.6433 32.8718C31.5918 32.8184 31.5307 32.7763 31.4636 32.748C31.3964 32.7197 31.3246 32.7057 31.2523 32.7068H29.0134L29.4775 29.6328H25.5452L25.0811 32.664Z',
  'M24.3745 21.6719C24.2758 21.6741 24.1818 21.7162 24.112 21.7896C24.0422 21.863 24.0021 21.962 24 22.0658V28.6248C24.0021 28.7286 24.0422 28.8275 24.112 28.9009C24.1818 28.9743 24.2758 29.0165 24.3745 29.0187H35.704C35.9801 29.0187 36.204 28.7948 36.204 28.5187V22.1719C36.204 21.8957 35.9801 21.6719 35.704 21.6719H24.3745Z',
  'M37.3509 23.6164L35.918 23.1797V27.5981L37.3509 27.1699C37.3867 27.1491 37.4196 27.1232 37.4486 27.0928C37.5006 27.0613 37.5442 27.0166 37.5755 26.9628C37.6068 26.9089 37.6248 26.8476 37.6277 26.7846V23.9931C37.6269 23.9072 37.5995 23.8238 37.5495 23.7558C37.4995 23.6878 37.4298 23.6388 37.3509 23.6164V23.6164Z'
];

/**
* Defines the Powered icon SVG generator function for assets of type 'Powered'.
*/
const poweredIcon = [
  'M26.0832 20.6212H35.778C35.9198 20.6195 36.0606 20.6472 36.1923 20.7028C36.3239 20.7583 36.4439 20.8406 36.5453 20.9449C36.6468 21.0492 36.7276 21.1736 36.7834 21.3108C36.8391 21.448 36.8687 21.5954 36.8702 21.7446V23.5566H36.8612V30.1543C36.8573 30.429 36.7588 30.6929 36.5841 30.8971C36.4113 31.099 36.1756 31.229 35.9198 31.2638V31.2777C35.8759 31.2862 35.8315 31.2915 35.787 31.2936H31.1584L31.1282 31.2111C31.0074 30.8496 30.7828 30.5365 30.4856 30.315C30.1883 30.0935 29.833 29.9746 29.4687 29.9746C29.1044 29.9746 28.7491 30.0935 28.4518 30.315C28.1545 30.5365 27.93 30.8496 27.8091 31.2111L27.782 31.2936H26.0832C25.7959 31.2936 25.5204 31.1736 25.3173 30.9599C25.1141 30.7463 25 30.4565 25 30.1543V21.7573C25 21.6078 25.028 21.4598 25.0825 21.3218C25.137 21.1837 25.2168 21.0583 25.3175 20.9528C25.4181 20.8473 25.5375 20.7636 25.6689 20.7067C25.8003 20.6498 25.9411 20.6207 26.0832 20.6212Z',
  'M29.4844 33.2414C30.2093 33.2414 30.797 32.6233 30.797 31.8609C30.797 31.0985 30.2093 30.4805 29.4844 30.4805C28.7595 30.4805 28.1719 31.0985 28.1719 31.8609C28.1719 32.6233 28.7595 33.2414 29.4844 33.2414Z'
];

/**
* Defines the Unpowered icon SVG generator function for assets of type 'Unpowered'.
*/
const unpoweredIcon = [
  'M34.0986 33.8129C34.9059 33.8129 35.5604 33.167 35.5604 32.3703C35.5604 31.5736 34.9059 30.9277 34.0986 30.9277C33.2912 30.9277 32.6367 31.5736 32.6367 32.3703C32.6367 33.167 33.2912 33.8129 34.0986 33.8129Z',
  'M39.7667 20.6215H21.2064C20.8865 20.6215 20.5796 20.7469 20.3534 20.9702C20.1271 21.1934 20 21.4963 20 21.812V30.5869C20 30.7431 20.0312 30.8978 20.0919 31.042C20.1526 31.1863 20.2415 31.3173 20.3536 31.4276C20.4656 31.5379 20.5987 31.6253 20.745 31.6847C20.8914 31.7442 21.0482 31.7746 21.2064 31.7742H23.784L23.8142 31.6913C23.9503 31.315 24.201 30.9895 24.5319 30.7594C24.8628 30.5293 25.2577 30.4057 25.6625 30.4057C26.0673 30.4057 26.4622 30.5293 26.7931 30.7594C27.124 30.9895 27.3747 31.315 27.5108 31.6913L27.541 31.7742H32.2256L32.2559 31.6913C32.3905 31.3135 32.6405 30.9863 32.9716 30.7549C33.3027 30.5234 33.6984 30.3991 34.1042 30.3991C34.5099 30.3991 34.9056 30.5234 35.2367 30.7549C35.5678 30.9863 35.8179 31.3135 35.9525 31.6913L35.9861 31.7742H40.8411L40.989 31.6282V23.112H41V21.812C41.0005 21.6533 40.9687 21.4961 40.9066 21.3497C40.8444 21.2032 40.7532 21.0706 40.6382 20.9596C40.5232 20.8486 40.3868 20.7615 40.2372 20.7034C40.0875 20.6453 39.9275 20.6175 39.7667 20.6215Z',
  'M25.665 33.8129C26.4723 33.8129 27.1268 33.167 27.1268 32.3703C27.1268 31.5736 26.4723 30.9277 25.665 30.9277C24.8576 30.9277 24.2031 31.5736 24.2031 32.3703C24.2031 33.167 24.8576 33.8129 25.665 33.8129Z'
];

/**
* Defines the Asset Trailer icon SVG generator function for assets of type 'Trailer'.
*/
const trailerIcon = [
  'M34.0986 33.8129C34.9059 33.8129 35.5604 33.167 35.5604 32.3703C35.5604 31.5736 34.9059 30.9277 34.0986 30.9277C33.2912 30.9277 32.6367 31.5736 32.6367 32.3703C32.6367 33.167 33.2912 33.8129 34.0986 33.8129Z',
  'M39.7667 20.6215H21.2064C20.8865 20.6215 20.5796 20.7469 20.3534 20.9702C20.1271 21.1934 20 21.4963 20 21.812V30.5869C20 30.7431 20.0312 30.8978 20.0919 31.042C20.1526 31.1863 20.2415 31.3173 20.3536 31.4276C20.4656 31.5379 20.5987 31.6253 20.745 31.6847C20.8914 31.7442 21.0482 31.7746 21.2064 31.7742H23.784L23.8142 31.6913C23.9503 31.315 24.201 30.9895 24.5319 30.7594C24.8628 30.5293 25.2577 30.4057 25.6625 30.4057C26.0673 30.4057 26.4622 30.5293 26.7931 30.7594C27.124 30.9895 27.3747 31.315 27.5108 31.6913L27.541 31.7742H32.2256L32.2559 31.6913C32.3905 31.3135 32.6405 30.9863 32.9716 30.7549C33.3027 30.5234 33.6984 30.3991 34.1042 30.3991C34.5099 30.3991 34.9056 30.5234 35.2367 30.7549C35.5678 30.9863 35.8179 31.3135 35.9525 31.6913L35.9861 31.7742H40.8411L40.989 31.6282V23.112H41V21.812C41.0005 21.6533 40.9687 21.4961 40.9066 21.3497C40.8444 21.2032 40.7532 21.0706 40.6382 20.9596C40.5232 20.8486 40.3868 20.7615 40.2372 20.7034C40.0875 20.6453 39.9275 20.6175 39.7667 20.6215Z',
  'M25.665 33.8129C26.4723 33.8129 27.1268 33.167 27.1268 32.3703C27.1268 31.5736 26.4723 30.9277 25.665 30.9277C24.8576 30.9277 24.2031 31.5736 24.2031 32.3703C24.2031 33.167 24.8576 33.8129 25.665 33.8129Z'
];

/**
 * Defines a mapping of asset types to their Detailed Asset Type icon factory
 * functions.
 */
const assetTypeIconMap = {
  [AssetType.Vehicle]   : vehicleIcon,
  [AssetType.Machine]   : machineIcon,
  [AssetType.Tool]      : toolIcon,
  [AssetType.Powered]   : poweredIcon,
  [AssetType.Unpowered] : unpoweredIcon,
  [AssetType.Trailer]   : trailerIcon
};

/**
 * Defines the dls color mappings for the detail marker background default and hovered
 * & selected states.
 */
const bgColorMap = {
  default : white,
  hovered : black
};

/**
 * Defines the dls color mappings for the detail marker font default and hovered
 * & selected states.
 */
const iconColorMap = {
  default : grey4,
  hovered : white
};

/**
 * Defines the dls color mappings for the detail marker font default and hovered
 * & selected states.
 */
const fontColorMap = {
  default : black,
  hovered : white
};

/**
 * SVG asset label style
 */
const fontStyle = {
  'font-family'    : 'Arial, Helvetica, sans-serif',
  'font-weight'    : 500,
  'font-style'     : 'normal',
  'font-size'      : '14px',
  'line-height'    : '18px',
  'align-items'    : 'center',
  'text-align'     : 'center',
  'letter-spacing' : '1px',
  'text-transform' : 'uppercase'
};

/**
 * Manages converting the asset name to a displayable svg text string.
 */
const getText = ({assetName, name}: AssetMarker): string => {
  const labelName     = assetName || name;
  const {lengthLimit} = defaultFactor;
  const label         = labelName?.length > lengthLimit ? `${labelName?.substring(0, lengthLimit)} ...` : (labelName || '...');

  return label;
};

/**
 * Dynamically determines the width and height of the Asset Marker based on the
 * length of the Asset name.
 */
const getContainerDimension = (asset: AssetMarker): {width: number, height: number} => {
  const {width:w, height:h} = AssetMarkerSize;
  const {lengthLimit, minimumHeight, charWidth, label: labelFactor} = defaultFactor;
  const name        = asset.assetName || asset.name;
  const length      = name.length > lengthLimit ? lengthLimit + 1 : name.length;
  const labelWidth  = (length * charWidth);
  const width       = labelWidth + w;
  const labelHeight = labelWidth + h - labelFactor;
  const height      = labelHeight < minimumHeight ? minimumHeight : labelHeight;
  return {width, height};
};

/**
 * Dynamically determines the position of some components
 * [viewBox, icon, scale, type, text]
 */
const positions = {
  1 : {
    view  : 0,
    icon  : 0,
    scale : 0,
    type  : 0,
    text  : 1,
    minX  : 1
  },
  2 : {
    view  : 0,
    icon  : 2,
    scale : 0.05,
    type  : 0,
    text  : 2,
    minX  : 1
  },
  3 : {
    view  : 7,
    icon  : 9,
    scale : 0.14,
    type  : 0,
    text  : 3,
    minX  : 1
  },
  4 : {
    view  : 15,
    icon  : 16,
    scale : 0.23,
    type  : 1,
    text  : 4,
    minX  : 1
  },
  5 : {
    view  : 30,
    icon  : 19,
    scale : 0.28,
    type  : 1,
    text  : 5,
    minX  : 1
  },
  6 : {
    view  : 40,
    icon  : 25,
    scale : 0.35,
    type  : 1,
    text  : 6,
    minX  : 2
  },
  7 : {
    view  : 45,
    icon  : 32,
    scale : 0.43,
    type  : 2,
    text  : 7,
    minX  : 4
  },
  8 : {
    view  : 60,
    icon  : 37,
    scale : 0.51,
    type  : 3,
    text  : 8,
    minX  : 4
  },
  9 : {
    view  : 70,
    icon  : 43,
    scale : 0.59,
    type  : 4,
    text  : 9,
    minX  : 4
  },
  10 : {
    view  : 85,
    icon  : 50,
    scale : 0.69,
    type  : 5,
    text  : 10,
    minX  : 4
  },
  11 : {
    view  : 90,
    icon  : 55,
    scale : 0.74,
    type  : 7,
    text  : 11,
    minX  : 6
  },
  12 : {
    view  : 105,
    icon  : 62,
    scale : 0.83,
    type  : 7,
    text  : 12,
    minX  : 6
  },
  13 : {
    view  : 115,
    icon  : 69,
    scale : 0.94,
    type  : 8,
    text  : 13,
    minX  : 7
  },
  14 : {
    view  : 125,
    icon  : 76,
    scale : 1.03,
    type  : 9,
    text  : 15,
    minX  : 8
  },
  15 : {
    view  : 135,
    icon  : 79,
    scale : 1.05,
    type  : 10,
    text  : 16,
    minX  : 7
  },
  16 : {
    view  : 145,
    icon  : 83,
    scale : 1.12,
    type  : 10,
    text  : 17,
    minX  : 7
  },
  17 : {
    view  : 155,
    icon  : 88,
    scale : 1.18,
    type  : 11,
    text  : 18,
    minX  : 8
  },
  18 : {
    view  : 169,
    icon  : 92,
    scale : 1.22,
    type  : 11,
    text  : 19,
    minX  : 8
  },
  19 : {
    view  : 178,
    icon  : 99,
    scale : 1.34,
    type  : 12,
    text  : 18,
    minX  : 7
  },
  20 : {
    view  : 188,
    icon  : 99,
    scale : 1.36,
    type  : 13,
    text  : 19,
    minX  : 4
  },
  [defaultPosition?.lastPosition] : {
    view  : 207,
    icon  : 118,
    scale : 1.57,
    type  : 16,
    text  : 20,
    minX  : 9
  }
};

/**
 * Defines the detailed Asset Marker which is used to render the background svg
 * for all Asset Markers when being viewed in detailed mode. This function accepts
 * an icon based on the asset type, and renders the Asset Marker with the given
 * asset type icon.
 */
const createMarkerGraphic = (
  asset: AssetMarker,
  assetIconPaths: string[],
  isHover = false,
  markerOnly = false,
  includeIcon = true
): string => { // eslint-disable-line max-params
  const {
    assetName,
    name: alias,
    iconStatus,
    drawCustomIcon,
    customIconProps = {}
  } = asset;
  const name         = assetName || alias;
  const lastPosition = defaultPosition?.lastPosition;
  const position     = name?.length <= lastPosition ? name?.length : lastPosition;
  const state        = !isHover ? 'default' : 'hovered';
  const bg           = bgColorMap[state];
  const color        = iconColorMap[state];
  const status       = iconStatus || assetStateColorMap[getAssetState(asset as BaseAsset)] || IconStatus.offline;
  const {width, height} = getContainerDimension(asset);
  const {view, icon : point, scale, type, text, minX} = positions[position];
  const label = getText(asset);

  // Root SVG container
  const draw = SVG()
    .size(width, height)
    .viewbox(minX, 8,
      defaultPosition?.viewBox + view, 78);

  // Label Container
  if (!markerOnly) {
    const labelContainerGroup = draw.group().filterWith(function(add) {
      const blur = add
        .flood('#ffffff', 0)
        .colorMatrix('matrix', '0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0')
        .in(add.$sourceAlpha)
        .offset(2, 2)
        .gaussianBlur(2, 0)
        .colorMatrix('matrix', '0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0');
      add.blend(add.$source, blur, 'normal');
      this.size(width, height).move(0, 0);
    })
      .transform({scaleX: defaultPosition?.scale + scale});

    labelContainerGroup
      .path('M14 8C11.7909 8 10 9.79086 10 12V41.863C10 44.0721 11.7909 45.863 14 45.863H70.6355L74.8912 51.0274C74.9932 51.1511 75.128 51.2564 75.2865 51.336C75.8989 51.6438 76.7148 51.5056 77.1089 51.0274L81.3647 45.863H134C136.209 45.863 138 44.0721 138 41.863V12C138 9.79086 136.209 8 134 8H14Z')
      .fill(bg);

    // Label
    draw
      .text(label)
      .font({anchor: 'middle'})
      .css(fontStyle)
      .move(
        (includeIcon ? defaultPosition?.textWithIcon : defaultPosition?.textWithoutIcon) + text,
        18
      )
      .fill(fontColorMap[state]);

    if (includeIcon) {
      // default case
      if (!drawCustomIcon) {
        // icon Container
        const iconContainer = draw.nested().move(defaultPosition?.type + type, 0);

        // drwaing icon paths...
        assetIconPaths.forEach((path) => {
          iconContainer
            .path(path).fill(color);
        });
        // Tool icon is only icon that contains a <rect> with its <paths>.
        // The logic below allows us to loop through the <paths> and tack on
        // the additional <rect> separately.
        asset.assetType === AssetType.Tool && iconContainer.rect(2.38381, 1.19191).fill(color).move(38.3047, 24.2891);
      } else {
        drawCustomIcon({
          draw,
          color,
          ...customIconProps
        });
      }
    }
  }

  //Marker Container
  const markerContainer = draw
    .nested()
    .move(defaultPosition?.icon + point, 26);

  const markerDefs = markerContainer.defs();

  // Marker Circle
  const markerCircle = markerDefs
    .path('M28.99 52.51C20.86 52.51 14.27 46.22 14.27 38.47C14.27 30.72 20.86 24.43 28.99 24.43C37.11 24.43 43.71 30.72 43.71 38.47C43.71 46.22 37.11 52.51 28.99 52.51Z')
    .fill(bg)
    .stroke('#c8c3bf')
    .id('marker-circle');

  // Arrow Marker
  const marker = markerDefs
    .path('M 39 43 L 29 40 L 19 43 L 24 37.4 L 29 32 L 34.1 37.4 L 39 43 Z')
    .fill(status)
    .rotate(asset.heading, 29, 39)
    .id('marker');

  markerContainer.use(markerCircle);
  markerContainer.use(marker);
  const serializedSVG = new XMLSerializer().serializeToString(draw.node);
  const url = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(serializedSVG.trim())))}`;
  return url;
};

/**
 * Creates an individual Graphic objects based on the given assets, where
 * the Graphic depicts the asset in it's current location.
 *
 * ```typescript
 *
 *   import {createMarker} from '@ion/dls/src/maps/markers';
 *
 *   loadModules([
 *     'esri/layers/GraphicsLayer',
 *     'esri/Graphic'
 *   ])
 *     .then(([GraphicsLayer, Graphic]) => {
 *       const point = createPoint(selectedAsset);
 *       const marker = createMarker({
 *         asset       : point as any,
 *         markerOnly  : false,
 *         includeIcon : false,
 *         Graphic,
 *         color,
 *         selectedAsset
 *       });
 *     }
 *
 * ```
 *
 */
export const createMarker = ({
  asset,
  Graphic,
  color,
  selectedAsset = null,
  markerOnly = false,
  includeIcon = true
}: CreateMarkerParams) => {
  if (Graphic) {
    const {geometry} = asset;
    const {width, height} = AssetMarkerSize;
    const currentId   = asset.id || asset.assetId;
    const selectedId  = selectedAsset?.id || selectedAsset?.assetId;
    const isSameAsset = currentId === selectedId;
    const [longitude, latitude] = geometry.coordinates;
    const state = isSameAsset ? AssetMarkerStates.SELECTED : AssetMarkerStates.DEFAULT;
    return new Graphic({
      geometry : {
        type : 'point',
        latitude,
        longitude
      },
      attributes : {...asset},
      symbol     : {
        type : 'picture-marker',
        url  : createMarkerGraphic(
          asset,
          assetTypeIconMap[asset?.assetType] || assetTypeIconMap[AssetType.Vehicle],
          state === AssetMarkerStates.SELECTED,
          markerOnly,
          includeIcon
        ),
        width,
        height
      }
    });
  }
};

/**
 * Creates a collection of Graphics objects based on a collection of assets,
 * where each Graphic depicts an individual asset in it's current location.
 *
 * ```typescript
 *
 *   import {createMarkers} from '@ion/features/src/maps/markers';
 *
 *   loadModules([
 *     'esri/layers/GraphicsLayer',
 *     'esri/Graphic'
 *   ])
 *     .then(([GraphicsLayer, Graphic]) => {
 *       const points = createPoints(selectedAsset ? [selectedAsset] : assets);
 *       if (points.length) {
 *         const markers = createMarkers({
 *           assets      : points as any[],
 *           markerOnly  : false,
 *           includeIcon : false,
 *           Graphic,
 *           color,
 *           selectedAsset
 *         });
 *       }
 *     }
 *
 * ```
 *
 */
export const createMarkers = ({
  assets,
  Graphic,
  color,
  selectedAsset = null,
  markerOnly = false,
  includeIcon = true
}: CreateMarkersParams) => {
  if (Graphic) {
    return assets.map(asset => createMarker({
      asset, Graphic, color, selectedAsset, markerOnly, includeIcon
    }));
  }
};

export default createMarkers;
